import { useState } from 'react'
import { GraduationCap, ExternalLink } from 'lucide-react'
import adminApi from '../../../services/adminApi'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

const STATUS_STYLES = {
  pending: 'bg-yellow-500/20 text-yellow-300',
  verified: 'bg-green-500/20 text-green-300',
  rejected: 'bg-red-500/20 text-red-300',
}

const CertificationsTab = ({ certifications, onRefresh }) => {
  const [statusFilter, setStatusFilter] = useState('all')
  const [updatingId, setUpdatingId] = useState(null)
  const [editingNotesId, setEditingNotesId] = useState(null)
  const [notesValue, setNotesValue] = useState('')

  const filtered = certifications
    ? statusFilter === 'all'
      ? certifications
      : certifications.filter((c) => c.verificationStatus === statusFilter)
    : []

  const handleStatusChange = async (id, status, adminNotes) => {
    try {
      setUpdatingId(id)
      await adminApi.patch(`/api/admin/certifications/${id}/status`, { status, adminNotes })
      onRefresh?.()
    } catch (err) {
      console.error('Error updating cert status:', err)
    } finally {
      setUpdatingId(null)
    }
  }

  const handleSaveNotes = async (cert) => {
    await handleStatusChange(cert.id, cert.verificationStatus, notesValue)
    setEditingNotesId(null)
  }

  const counts = certifications
    ? {
        all: certifications.length,
        pending: certifications.filter((c) => c.verificationStatus === 'pending').length,
        verified: certifications.filter((c) => c.verificationStatus === 'verified').length,
        rejected: certifications.filter((c) => c.verificationStatus === 'rejected').length,
      }
    : { all: 0, pending: 0, verified: 0, rejected: 0 }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
        <h3 className="text-xl font-semibold text-white flex items-center gap-2">
          <GraduationCap className="w-6 h-6" />
          Certifications
        </h3>
        <div className="flex items-center gap-2">
          {['all', 'pending', 'verified', 'rejected'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === s
                  ? 'bg-purple-600 text-white'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)} ({counts[s]})
            </button>
          ))}
        </div>
      </div>

      {!filtered.length ? (
        <div className="text-center py-16 text-gray-400">
          <GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No certifications found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-white/10">
            <thead>
              <tr>
                {['Facilitator', 'Email', 'Certification', 'Issuer', 'Document', 'Status', 'Admin Notes', 'Date'].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {filtered.map((cert) => (
                <tr key={cert.id} className="hover:bg-white/5">
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-white">
                    {cert.facilitatorName || 'N/A'}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                    <a href={`mailto:${cert.facilitatorEmail}`} className="hover:text-white transition-colors">
                      {cert.facilitatorEmail}
                    </a>
                  </td>
                  <td className="px-4 py-4 text-sm font-medium text-white max-w-[200px] truncate">
                    {cert.name}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">{cert.issuer}</td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    {cert.documentUrl ? (
                      <a
                        href={`${API_BASE_URL}${cert.documentUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-purple-400 hover:text-purple-300"
                      >
                        View <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-gray-500">None</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <select
                      value={cert.verificationStatus}
                      onChange={(e) => handleStatusChange(cert.id, e.target.value, cert.adminNotes)}
                      disabled={updatingId === cert.id}
                      className={`rounded-full px-3 py-1 text-xs font-medium border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                        STATUS_STYLES[cert.verificationStatus] || 'bg-gray-500/20 text-gray-300'
                      }`}
                      style={{ backgroundColor: 'transparent' }}
                    >
                      <option value="pending" className="bg-slate-800 text-white">Pending</option>
                      <option value="verified" className="bg-slate-800 text-white">Verified</option>
                      <option value="rejected" className="bg-slate-800 text-white">Rejected</option>
                    </select>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-300 max-w-[200px]">
                    {editingNotesId === cert.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={notesValue}
                          onChange={(e) => setNotesValue(e.target.value)}
                          className="px-2 py-1 bg-slate-700 border border-white/20 text-white rounded text-xs w-full"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveNotes(cert)
                            if (e.key === 'Escape') setEditingNotesId(null)
                          }}
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveNotes(cert)}
                          className="text-xs text-green-400 hover:text-green-300 whitespace-nowrap"
                        >
                          Save
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingNotesId(cert.id)
                          setNotesValue(cert.adminNotes || '')
                        }}
                        className="text-left hover:text-white transition-colors truncate block w-full"
                      >
                        {cert.adminNotes || <span className="text-gray-500 italic">Add note...</span>}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                    {new Date(cert.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default CertificationsTab
